<script lang="ts">
	import type { HTMLInputAttributes } from "svelte/elements";
	import type { WithElementRef } from "bits-ui";
	import { cn } from "#lib/web-ui/ui/utils.js";
	import { browser } from "$app/env";
	import { onMount } from "svelte";
	import { loadPlacesLibrary } from "@fergmar/ui/places/loader";
	import type { PlacesBounds } from "@fergmar/ui/places/bounds";
	import { placeComponents, type PlaceComponents } from "@fergmar/ui/places/components";

	let {
		ref = $bindable(null),
		value = $bindable(),
		class: className,
		placesToken,
		bounds,
		strictBounds = true,
		onPlaceSelect,
		...restProps
	}: WithElementRef<HTMLInputAttributes> & {
		placesToken: string;
		bounds?: PlacesBounds;
		strictBounds?: boolean;
		onPlaceSelect?: (place: {
			address: string;
			streetNumber: string;
			route: string;
			lat: number;
			lng: number;
			components: PlaceComponents;
		}) => void;
	} = $props();

	let isPlacesLoaded = $state(false);
	let isLoadingPlaces = $state(false);

	function initGooglePlaces() {
		if (browser && !isPlacesLoaded && !isLoadingPlaces) {
			isLoadingPlaces = true;
			loadPlacesLibrary(placesToken)
				.then((places) => {
					isPlacesLoaded = true;
					const autocomplete = new places.Autocomplete(ref as HTMLInputElement, {
						fields: ["address_components", "geometry"],
						types: ["address"],
						...(bounds ? { bounds, strictBounds } : {}),
						componentRestrictions: { country: "us" }
					});

					autocomplete.addListener("place_changed", () => {
						const unformattedAddress = autocomplete.getPlace();

						if (unformattedAddress && unformattedAddress.address_components) {
							const address = unformattedAddress.address_components.reduce<
								Record<string, string>
							>(
								(
									acc: Record<string, string>,
									component: google.maps.GeocoderAddressComponent
								) => {
									const types = component.types;

									if (types.includes("street_number")) {
										acc.streetNumber = component.long_name;
									} else if (types.includes("route")) {
										acc.streetName = component.short_name;
									} else if (types.includes("locality")) {
										acc.city = component.long_name;
									} else if (types.includes("administrative_area_level_1")) {
										acc.state = component.short_name;
									} else if (types.includes("postal_code")) {
										acc.zipCode = component.long_name;
									} else if (types.includes("subpremise")) {
										acc.subpremise = component.long_name;
									}

									return acc;
								},
								{
									streetNumber: "",
									streetName: "",
									city: "",
									state: "",
									zipCode: "",
									subpremise: ""
								}
							);

							const formattedAddress =
								`${address.streetNumber} ${address.streetName}, ${address.city}, ${address.state} ${address.zipCode}`.trim();
							value = formattedAddress;

							if (onPlaceSelect && unformattedAddress.geometry?.location) {
								onPlaceSelect({
									address: formattedAddress,
									streetNumber: address.streetNumber,
									route: address.streetName,
									lat: unformattedAddress.geometry.location.lat(),
									lng: unformattedAddress.geometry.location.lng(),
									components: placeComponents(address)
								});
							}
						} else {
							value = "";
						}
					});

					setTimeout(() => {
						const pacContainer = document.querySelector(".pac-container");
						if (pacContainer) {
							(pacContainer as HTMLElement).style.zIndex = "9999";
							(pacContainer as HTMLElement).style.position = "absolute";
						}
					}, 100);
				})
				.catch((error) => {
					console.error("Error loading Google Maps library:", error);
				});
		}
	}

	onMount(() => {
		const style = document.createElement("style");
		style.textContent = `
			.pac-container {
				z-index: 9999 !important;
				position: absolute !important;
				background-color: white !important;
				border: 1px solid #ccc !important;
				border-radius: 4px !important;
				box-shadow: 0 2px 10px rgba(0,0,0,0.2) !important;
			}
			
			.pac-item {
				cursor: pointer !important;
				padding: 8px 12px !important;
				border-bottom: 1px solid #eee !important;
				pointer-events: auto !important;
			}
			
			.pac-item:hover {
				background-color: #f5f5f5 !important;
			}
			
			.pac-item-selected {
				background-color: #e6f3ff !important;
			}
			
			.pac-matched {
				font-weight: bold !important;
			}
		`;
		document.head.appendChild(style);

		return () => {
			document.head.removeChild(style);
		};
	});
</script>

<!-- Wrapper div to ensure proper positioning -->
<div class="relative">
	<!-- Input Field with Google Places Autocomplete -->
	<input
		bind:this={ref}
		data-sentry-ignore
		class={cn(
			"fborder-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
			className
		)}
		bind:value
		onfocus={initGooglePlaces}
		onclick={initGooglePlaces}
		onkeydown={(e) => {
			if (e.key === "Enter") {
				e.preventDefault();
			}
		}}
		{...restProps}
	/>
</div>
